import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useNavigate, useSelector } from "../../../../imports/generalImport";
import { actionNotifier } from "../../../../components/tostify";
import ModelLoading from "../../../../ModelLoading";
import { purchaseProduct } from "../../../../apiCalls/urlFunction";
import { fetchUserProfile } from "../../../../store/userProfile/action";
import { useAuth } from "../../../../hooks/contexts/AuthContext";
import { selectOrderSummary } from "../../../../store/denominations/selectors";
import CheckoutPage from "../../../PAYMENTS/CheckoutPage";

// ============================================================
// CONSTANTS
// ============================================================

const PAYMENT_CONFIG = {
  MAX_CLICKS: 2,
  DISABLE_DURATION: 5000, // 5 seconds
  LOADING_TIMEOUT: 1500,
  MODAL_CLOSE_DELAY: 2000
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function UserPayButton({ receiverData, setReceiverErrorOpen, walletPayment }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  // --- Selectors ---
  const userBalance = useSelector((state) => state?.UserProfile?.allData);
  const product = useSelector((state) => state?.ProductDetail?.allData || {});
  const { netPayableWithBalance: netPayable, totalValue, filteredItems } =
    useSelector(selectOrderSummary);

  // --- State ---
  const [clickCount, setClickCount] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState({ active: false, message: "" });
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState("");

  // ============================================================
  // PAYMENT HANDLERS
  // ============================================================

  const handlePaymentSuccess = (response) => {
    actionNotifier.success("Payment successful! Redirecting...");
    dispatch(fetchUserProfile());

    // Close modal after delay
    setTimeout(() => {
      setPaymentData(null);
      navigate("/profile/order", { replace: true });
    }, PAYMENT_CONFIG.MODAL_CLOSE_DELAY);
  };

  const handlePaymentError = (error) => {
    actionNotifier.error(error?.message || "Payment failed. Please try again.");
    setPaymentData(null);
  };

  const handleModalClose = () => {
    if (loading.active) {
      // Don't close if payment is processing
      return;
    }
    setPaymentData(null);
    setError("");
  };

  // ============================================================
  // PAYLOAD GENERATION
  // ============================================================

  const prepareDenominations = (items) => {
    if (!items?.length) return [];

    return items
      .filter((item) => item?.QUANTITY > 0)
      .map((item) => {
        const baseDenom = {
          denominationId: item?.DENOMINATION_ID || null,
          quantity: item?.QUANTITY || 0,
        };

        // Handle range denomination
        if (product?.rangeDenomination) {
          return {
            ...baseDenom,
            selectedAmount: String(item?.AMOUNT),
          };
        }

        return baseDenom;
      });
  };

  const createPaymentPayload = () => {
    const denominations = prepareDenominations(filteredItems);

    return {
      ...receiverData,
      productId: String(product?.id || ""),
      denominations,
      walletDeduct: walletPayment,
    };
  };

  // ============================================================
  // VALIDATION
  // ============================================================

  const validatePayment = () => {
    // Check items
    if (!filteredItems?.length) {
      actionNotifier.error("Please select a denomination.");
      return false;
    }

    // Check receiver
    if (!receiverData?.phoneNumber?.trim()) {
      setReceiverErrorOpen?.(true);
      return false;
    }

    // Check total
    if (totalValue <= 0) {
      actionNotifier.error("Invalid amount.");
      return false;
    }

    return true;
  };

  // ============================================================
  // PAYMENT PROCESS
  // ============================================================

  const processPayment = async () => {
    if (loading.active) return;

    // Validate
    if (!validatePayment()) return;

    // Start loading
    setLoading({
      active: true,
      message: "Processing your payment securely...",
    });

    try {
      const payload = createPaymentPayload();
      const response = await purchaseProduct(payload);

      // Handle API errors
      if (!response?.status) {
        throw new Error(response?.message || "Payment initiation failed.");
      }

      // Handle redirect payment
      if (response?.data?.redirectURL) {
        window.location.href = response.data.redirectURL;
        return;
      }

      // Handle widget payment
      if (response?.data?.checkoutId && response?.data?.integrityId) {
        setPaymentData(response);
        setError("");
      } else {
        throw new Error("Invalid payment response.");
      }
    } catch (error) {
      const errorMessage = error?.message || "Payment failed. Please try again.";
      actionNotifier.error(errorMessage);
      setError(errorMessage);
      setPaymentData(null);
    } finally {
      // Clear loading state after timeout
      setTimeout(() => {
        setLoading({ active: false, message: "" });
      }, PAYMENT_CONFIG.LOADING_TIMEOUT);
    }
  };

  // ============================================================
  // CLICK HANDLER
  // ============================================================

  const handleClick = () => {
    // Check disabled state
    if (isDisabled) return;

    // Check login
    if (!isLoggedIn) {
      navigate("/login", { replace: true });
      return;
    }

    // Anti-spam: track clicks
    const newCount = clickCount + 1;
    setClickCount(newCount);

    if (newCount >= PAYMENT_CONFIG?.MAX_CLICKS) {
      setIsDisabled(true);

      setTimeout(() => {
        setClickCount(0);
        setIsDisabled(false);
      }, PAYMENT_CONFIG.DISABLE_DURATION);
    }

    // Process payment
    processPayment();
  };

  // ============================================================
  // RENDER
  // ============================================================

  const isButtonDisabled =
    totalValue <= 0 || loading.active || isDisabled;

  return (
    <>
      {/* Loading Overlay */}
      <ModelLoading
        loadingStatus={loading.active}
        message={loading.message}
      />

      {/* Pay Button */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Button
          variant="contained"
          disabled={isButtonDisabled}
          // disabled={true}
          onClick={handleClick}
          sx={{
            width: {
              xs: "100%",
              sm: "100%",
              md: "60%",
              lg: "400px",
            },
            minHeight: { xs: 52, lg: 48 },
            fontSize: { xs: "1.25rem", lg: "1.4rem" },
            fontWeight: 700,
            fontFamily: "Be Vietnam Pro",
            border: "1.5px solid #002865",
            borderRadius: 4,
            color: totalValue ? "#FFF" : "text.secondary",
            backgroundColor: totalValue ? "primary.main" : "action.disabledBackground",
            "&:hover": {
              backgroundColor: totalValue ? "primary.main" : "action.disabledBackground",
              boxShadow: 5,
            },
            "&.Mui-disabled": {
              backgroundColor: "action.disabledBackground",
              color: "text.disabled",
            },
          }}
        >
          {isLoggedIn ? "Get This Card" : "Login & Pay"}
        </Button>
      </Box>

      {/* Error Message */}
      {error && (
        <Typography
          color="error"
          variant="subtitle2"
          sx={{ mt: 1, textAlign: "center" }}
        >
          {error}
        </Typography>
      )}

      {/* Payment Modal */}
      <Dialog
        open={Boolean(paymentData)}
        onClose={() => setPaymentData(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '95vh',
            height: '100vh',
            margin: 0,
            borderRadius: 4,
            bgcolor: '#ffffff',
            boxShadow: 'none'
          }
        }}
      >
        {paymentData && (
          <CheckoutPage
            orderData={paymentData}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            onClose={() => setPaymentData(null)}
          />
        )}
      </Dialog>
    </>
  );
}
